import { createFileRoute, getRouteApi } from "@tanstack/react-router";
import { Suspense } from "react";

import { useViewer } from "../data/hooks.ts";
import { HouseholdGeneral } from "../settings/household-general.tsx";
import { Members } from "../settings/members/members.tsx";
import { Profile } from "../settings/profile.tsx";
import { sectionLabels } from "../settings/section-labels.ts";
import { loadSection } from "../settings/section-loader.ts";
import { SectionPending } from "../settings/section-pending.tsx";
import { canOpenSection, settingsSectionSchema } from "../settings/sections.ts";
import { Security } from "../settings/security.tsx";
import { lazyWithPreload } from "../shell/lazy.ts";
import { PageHeading } from "../shell/page-heading.tsx";
import { PlaceholderPage } from "../shell/placeholder-page.tsx";
import { parameterOrNotFound } from "../shell/route-parameters.ts";
import { SkeletonLines } from "../shell/skeletons.tsx";
import { NoAccessPage } from "../shell/status-pages.tsx";

import type { SettingsSection } from "../settings/sections.ts";
import type { ReactElement } from "react";

const route = getRouteApi("/_app/settings/$section");

const FeedManager = lazyWithPreload(async () => {
  const { FeedManager: component } = await import("../calendar/feed-dialog.tsx");
  return component;
});

const NotificationSettings = lazyWithPreload(async () => {
  const { NotificationSettings: component } = await import("../settings/notification-settings.tsx");
  return component;
});

const RunningCostsSettings = lazyWithPreload(async () => {
  const { RunningCostsSettings: component } = await import("../settings/running-costs-settings.tsx");
  return component;
});

const AssetKinds = lazyWithPreload(async () => {
  const { AssetKinds: component } = await import("../assets/asset-kinds.tsx");
  return component;
});

// WHY: a section's code loads with its data in the loader, so it never opens on a second skeleton.
const lazyBodies: Partial<Record<SettingsSection, { readonly preload: () => Promise<void> }>> = {
  "calendar-feeds": FeedManager,
  "asset-kinds": AssetKinds,
  notifications: NotificationSettings,
  "running-costs": RunningCostsSettings,
};

const SettingsSectionPage = (): ReactElement => {
  const { section } = route.useParams();
  const viewer = useViewer();
  if (viewer === null || !canOpenSection(section, viewer)) {
    return <NoAccessPage />;
  }
  if (section === "members") {
    return <Members viewer={viewer} />;
  }
  if (
    section !== "profile" &&
    section !== "general" &&
    section !== "security" &&
    section !== "calendar-feeds" &&
    section !== "asset-kinds" &&
    section !== "running-costs" &&
    section !== "notifications"
  ) {
    return <PlaceholderPage />;
  }
  const bodies = {
    profile: <Profile viewer={viewer} />,
    general: <HouseholdGeneral viewer={viewer} />,
    security: <Security />,
    "calendar-feeds": (
      <Suspense fallback={<SkeletonLines />}>
        <FeedManager viewer={viewer} />
      </Suspense>
    ),
    "asset-kinds": (
      <Suspense fallback={<SkeletonLines />}>
        <AssetKinds viewer={viewer} />
      </Suspense>
    ),
    notifications: (
      <Suspense fallback={<SkeletonLines />}>
        <NotificationSettings viewer={viewer} />
      </Suspense>
    ),
    "running-costs": (
      <Suspense fallback={<SkeletonLines />}>
        <RunningCostsSettings viewer={viewer} />
      </Suspense>
    ),
  } as const;
  return (
    <div className="flex flex-col gap-6 p-4 expanded:max-w-180 expanded:p-0">
      <PageHeading />
      {bodies[section]}
    </div>
  );
};

export const Route = createFileRoute("/_app/settings/$section")({
  params: {
    parse: ({ section }) => ({ section: parameterOrNotFound(settingsSectionSchema, section) }),
    stringify: ({ section }) => ({ section }),
  },
  loader: async ({ context, params }) => {
    await Promise.all([loadSection(context.data, params.section), lazyBodies[params.section]?.preload()]);
    return { pageTitle: sectionLabels[params.section]() };
  },
  pendingComponent: () => <SectionPending section={route.useParams().section} />,
  component: SettingsSectionPage,
});
